import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Users, ShoppingCart, Handshake, Building2, Plus, Pencil, Calendar,
  Store, Home, Trash2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TODAY = new Date().toISOString().split("T")[0];
const MESES_NOME = ["Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const fmt = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function FuncionarioPublicoPage() {
  const { token } = useParams();
  const [func, setFunc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const now = new Date();
  const mes = now.getMonth() + 1;
  const ano = now.getFullYear();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/hub-empresa/funcionarios/publico/${token}`);
        setFunc(res.data);
      } catch (err) {
        if (err?.response?.status === 403) {
          setError("Conta bloqueada pelo administrador");
        } else {
          setError("Link invalido ou funcionario desativado");
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-8">
      <Toaster position="top-right" richColors />
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10"><Users className="w-8 h-8 text-primary" /></div>
                <div>
                  <h1 className="text-xl font-bold">Painel do Funcionario</h1>
                  <p className="text-sm text-muted-foreground">
                    Funcionario: <span className="font-semibold text-primary">{func.nome}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border/50">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold">{MESES_NOME[mes - 1]} / {ano}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Tabs (Modulos) */}
        <Tabs defaultValue="hub-empresa" className="w-full">
          <TabsList className="w-full grid grid-cols-2 sm:grid-cols-4">
            <TabsTrigger value="hub-empresa" className="gap-1.5" data-testid="tab-hub-empresa">
              <Building2 className="w-4 h-4" /> Hub Empresa
            </TabsTrigger>
            <TabsTrigger value="revenda" className="gap-1.5" data-testid="tab-revenda">
              <Store className="w-4 h-4" /> Revenda
            </TabsTrigger>
            <TabsTrigger value="acomodado" className="gap-1.5" data-testid="tab-acomodado">
              <Home className="w-4 h-4" /> Acomodado
            </TabsTrigger>
            <TabsTrigger value="rev-acomodado" className="gap-1.5" data-testid="tab-rev-acomodado">
              <Store className="w-4 h-4" /> Rev. Acomodado
            </TabsTrigger>
          </TabsList>

          <TabsContent value="hub-empresa" className="mt-4">
            <HubEmpresaSection token={token} mes={mes} ano={ano} />
          </TabsContent>
          <TabsContent value="revenda" className="mt-4">
            <RevendaSection token={token} />
          </TabsContent>
          <TabsContent value="acomodado" className="mt-4">
            <AcomodadoSection token={token} />
          </TabsContent>
          <TabsContent value="rev-acomodado" className="mt-4">
            <RevAcomodadoSection token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ================== HUB EMPRESA SECTION ==================
function HubEmpresaSection({ token, mes, ano }) {
  const [registros, setRegistros] = useState({ clientes: [], revendas: [], parceiros: [] });
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [clienteForm, setClienteForm] = useState({ nome: "", valor: "", data: TODAY });
  const [revendaForm, setRevendaForm] = useState({ servidor: "", quantidade: "", valor_unitario: "", data: TODAY, descricao: "" });
  const [parceiroForm, setParceiroForm] = useState({ nome: "", valor: "", data: TODAY, descricao: "" });

  const fetchRegistros = useCallback(async () => {
    try {
      const reg = await axios.get(`${API}/api/hub-empresa/funcionarios/publico/${token}/meus-registros?mes=${mes}&ano=${ano}`);
      setRegistros(reg.data);
    } catch {}
  }, [token, mes, ano]);

  useEffect(() => { fetchRegistros(); }, [fetchRegistros]);

  const submitCliente = async (e) => {
    e.preventDefault();
    if (!clienteForm.nome || !clienteForm.valor) return toast.error("Preencha nome e valor");
    setSubmitting(true);
    try {
      if (editing && editingType === "clientes") {
        await axios.put(`${API}/api/hub-empresa/funcionarios/publico/${token}/clientes/${editing.id}`, clienteForm);
        toast.success("Cliente atualizado!");
      } else {
        await axios.post(`${API}/api/hub-empresa/funcionarios/publico/${token}/clientes`, { ...clienteForm, mes, ano });
        toast.success("Cliente adicionado!");
      }
      setClienteForm({ nome: "", valor: "", data: TODAY });
      setEditing(null); setEditingType(null);
      fetchRegistros();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSubmitting(false); }
  };

  const submitRevenda = async (e) => {
    e.preventDefault();
    if (!revendaForm.servidor) return toast.error("Preencha o servidor");
    setSubmitting(true);
    try {
      if (editing && editingType === "revendas") {
        await axios.put(`${API}/api/hub-empresa/funcionarios/publico/${token}/revendas/${editing.id}`, revendaForm);
        toast.success("Revenda atualizada!");
      } else {
        await axios.post(`${API}/api/hub-empresa/funcionarios/publico/${token}/revendas`, { ...revendaForm, mes, ano });
        toast.success("Revenda adicionada!");
      }
      setRevendaForm({ servidor: "", quantidade: "", valor_unitario: "", data: TODAY, descricao: "" });
      setEditing(null); setEditingType(null);
      fetchRegistros();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSubmitting(false); }
  };

  const submitParceiro = async (e) => {
    e.preventDefault();
    if (!parceiroForm.nome || !parceiroForm.valor) return toast.error("Preencha nome e valor");
    setSubmitting(true);
    try {
      if (editing && editingType === "parceiros") {
        await axios.put(`${API}/api/hub-empresa/funcionarios/publico/${token}/parceiros/${editing.id}`, parceiroForm);
        toast.success("Parceiro atualizado!");
      } else {
        await axios.post(`${API}/api/hub-empresa/funcionarios/publico/${token}/parceiros`, { ...parceiroForm, mes, ano });
        toast.success("Parceiro adicionado!");
      }
      setParceiroForm({ nome: "", valor: "", data: TODAY, descricao: "" });
      setEditing(null); setEditingType(null);
      fetchRegistros();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSubmitting(false); }
  };

  const startEdit = (item, type) => {
    setEditing(item); setEditingType(type);
    if (type === "clientes") setClienteForm({ nome: item.nome, valor: item.valor, data: item.data });
    if (type === "revendas") setRevendaForm({ servidor: item.servidor, quantidade: item.quantidade, valor_unitario: item.valor_unitario, data: item.data, descricao: item.descricao || "" });
    if (type === "parceiros") setParceiroForm({ nome: item.nome, valor: item.valor, data: item.data, descricao: item.descricao || "" });
  };

  const handleDeleteRegistro = async (id, type) => {
    if (!window.confirm("Excluir este registro?")) return;
    try {
      await axios.delete(`${API}/api/hub-empresa/funcionarios/publico/${token}/${type}/${id}`);
      toast.success("Excluido!");
      fetchRegistros();
    } catch { toast.error("Erro ao excluir"); }
  };

  return (
    <Tabs defaultValue="clientes" className="w-full">
      <TabsList className="w-full grid grid-cols-3">
        <TabsTrigger value="clientes" className="gap-1.5"><Users className="w-4 h-4" /> Clientes</TabsTrigger>
        <TabsTrigger value="revendas" className="gap-1.5"><ShoppingCart className="w-4 h-4" /> Revendas</TabsTrigger>
        <TabsTrigger value="parceiros" className="gap-1.5"><Handshake className="w-4 h-4" /> Parceiros</TabsTrigger>
      </TabsList>

      <TabsContent value="clientes" className="space-y-4 mt-4">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">{editing && editingType === "clientes" ? "Editar" : "Novo"} Cliente Pago</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submitCliente} className="space-y-3">
              <div className="space-y-1"><Label>Nome *</Label><Input value={clienteForm.nome} onChange={e => setClienteForm({...clienteForm, nome: e.target.value})} /></div>
              <div className="space-y-1"><Label>Valor (R$) *</Label><Input type="number" step="0.01" value={clienteForm.valor} onChange={e => setClienteForm({...clienteForm, valor: e.target.value})} /></div>
              <div className="space-y-1"><Label>Data</Label><Input type="date" value={clienteForm.data} onChange={e => setClienteForm({...clienteForm, data: e.target.value})} /></div>
              <Button type="submit" disabled={submitting} className="w-full">{submitting ? "Salvando..." : editing && editingType === "clientes" ? "Atualizar" : "Adicionar"}</Button>
              {editing && editingType === "clientes" && <Button type="button" variant="outline" className="w-full" onClick={() => { setEditing(null); setEditingType(null); setClienteForm({ nome: "", valor: "", data: TODAY }); }}>Cancelar</Button>}
            </form>
          </CardContent>
        </Card>
        {registros.clientes.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Meus registros ({registros.clientes.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {registros.clientes.map(c => (
                <div key={c.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border/50">
                  <div><p className="text-sm font-medium">{c.nome}</p><p className="text-xs text-muted-foreground">{c.data} - {fmt(c.valor)}</p></div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(c, "clientes")} data-testid={`edit-cliente-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteRegistro(c.id, "clientes")} data-testid={`delete-cliente-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="revendas" className="space-y-4 mt-4">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">{editing && editingType === "revendas" ? "Editar" : "Nova"} Revenda</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submitRevenda} className="space-y-3">
              <div className="space-y-1"><Label>Servidor *</Label><Input value={revendaForm.servidor} onChange={e => setRevendaForm({...revendaForm, servidor: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Quantidade</Label><Input type="number" step="0.01" value={revendaForm.quantidade} onChange={e => setRevendaForm({...revendaForm, quantidade: e.target.value})} /></div>
                <div className="space-y-1"><Label>Valor Unitario</Label><Input type="number" step="0.01" value={revendaForm.valor_unitario} onChange={e => setRevendaForm({...revendaForm, valor_unitario: e.target.value})} /></div>
              </div>
              {revendaForm.quantidade && revendaForm.valor_unitario && (
                <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-center text-sm font-semibold">
                  Total: {fmt(parseFloat(revendaForm.quantidade || 0) * parseFloat(revendaForm.valor_unitario || 0))}
                </div>
              )}
              <div className="space-y-1"><Label>Data</Label><Input type="date" value={revendaForm.data} onChange={e => setRevendaForm({...revendaForm, data: e.target.value})} /></div>
              <div className="space-y-1"><Label>Descricao</Label><Textarea value={revendaForm.descricao} onChange={e => setRevendaForm({...revendaForm, descricao: e.target.value})} /></div>
              <Button type="submit" disabled={submitting} className="w-full">{submitting ? "Salvando..." : editing && editingType === "revendas" ? "Atualizar" : "Adicionar"}</Button>
              {editing && editingType === "revendas" && <Button type="button" variant="outline" className="w-full" onClick={() => { setEditing(null); setEditingType(null); setRevendaForm({ servidor: "", quantidade: "", valor_unitario: "", data: TODAY, descricao: "" }); }}>Cancelar</Button>}
            </form>
          </CardContent>
        </Card>
        {registros.revendas.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Meus registros ({registros.revendas.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {registros.revendas.map(r => (
                <div key={r.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border/50">
                  <div><p className="text-sm font-medium">{r.servidor}</p><p className="text-xs text-muted-foreground">{r.data} - {fmt(r.valor_total)}</p></div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(r, "revendas")} data-testid={`edit-hub-revenda-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteRegistro(r.id, "revendas")} data-testid={`delete-hub-revenda-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="parceiros" className="space-y-4 mt-4">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">{editing && editingType === "parceiros" ? "Editar" : "Novo"} Parceiro</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submitParceiro} className="space-y-3">
              <div className="space-y-1"><Label>Nome *</Label><Input value={parceiroForm.nome} onChange={e => setParceiroForm({...parceiroForm, nome: e.target.value})} /></div>
              <div className="space-y-1"><Label>Valor (R$) *</Label><Input type="number" step="0.01" value={parceiroForm.valor} onChange={e => setParceiroForm({...parceiroForm, valor: e.target.value})} /></div>
              <div className="space-y-1"><Label>Data</Label><Input type="date" value={parceiroForm.data} onChange={e => setParceiroForm({...parceiroForm, data: e.target.value})} /></div>
              <div className="space-y-1"><Label>Descricao</Label><Textarea value={parceiroForm.descricao} onChange={e => setParceiroForm({...parceiroForm, descricao: e.target.value})} /></div>
              <Button type="submit" disabled={submitting} className="w-full">{submitting ? "Salvando..." : editing && editingType === "parceiros" ? "Atualizar" : "Adicionar"}</Button>
              {editing && editingType === "parceiros" && <Button type="button" variant="outline" className="w-full" onClick={() => { setEditing(null); setEditingType(null); setParceiroForm({ nome: "", valor: "", data: TODAY, descricao: "" }); }}>Cancelar</Button>}
            </form>
          </CardContent>
        </Card>
        {registros.parceiros.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Meus registros ({registros.parceiros.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {registros.parceiros.map(p => (
                <div key={p.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border/50">
                  <div><p className="text-sm font-medium">{p.nome}</p><p className="text-xs text-muted-foreground">{p.data} - {fmt(p.valor)}</p></div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(p, "parceiros")} data-testid={`edit-parceiro-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteRegistro(p.id, "parceiros")} data-testid={`delete-parceiro-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </TabsContent>
    </Tabs>
  );
}

// ================== REVENDA SECTION ==================
function RevendaSection({ token }) {
  const [revendas, setRevendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRevendaId, setSelectedRevendaId] = useState("");
  const [editingLanc, setEditingLanc] = useState(null);
  const [form, setForm] = useState({ tipo_credito: "MEC", mec_creditos: "", data: TODAY, servico: "", valor: "", moeda: "R$", descricao: "" });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/hub-empresa/funcionarios/publico/${token}/revenda/list`);
      setRevendas(res.data);
    } catch { toast.error("Erro ao carregar"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setEditingLanc(null);
    setSelectedRevendaId("");
    setForm({ tipo_credito: "MEC", mec_creditos: "", data: TODAY, servico: "", valor: "", moeda: "R$", descricao: "" });
  };

  const openNovo = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (lanc, revendaId) => {
    setEditingLanc(lanc);
    setSelectedRevendaId(revendaId);
    setForm({
      tipo_credito: lanc.tipo_credito || "MEC",
      mec_creditos: lanc.mec_creditos || "",
      data: lanc.data || TODAY,
      servico: lanc.servico || "",
      valor: String(lanc.valor || ""),
      moeda: lanc.moeda || "R$",
      descricao: lanc.descricao || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingLanc && !selectedRevendaId) return toast.error("Selecione a revenda");
    if (!form.servico || !form.valor) return toast.error("Preencha servico e valor");
    setSaving(true);
    try {
      if (editingLanc) {
        await axios.put(`${API}/api/hub-empresa/funcionarios/publico/${token}/revenda/lancamento/${editingLanc.id}`, {
          ...form,
          valor: parseFloat(form.valor),
        });
        toast.success("Lancamento atualizado!");
      } else {
        await axios.post(`${API}/api/hub-empresa/funcionarios/publico/${token}/revenda/lancamento`, {
          ...form,
          revenda_id: selectedRevendaId,
          valor: parseFloat(form.valor),
        });
        toast.success("Lancamento adicionado!");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (lanc) => {
    if (!window.confirm(`Excluir lancamento "${lanc.servico || ""}"?`)) return;
    try {
      await axios.delete(`${API}/api/hub-empresa/funcionarios/publico/${token}/revenda/lancamento/${lanc.id}`);
      toast.success("Excluido!");
      fetchData();
    } catch { toast.error("Erro ao excluir"); }
  };

  if (loading) return <p className="text-center py-8 text-muted-foreground">Carregando...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{revendas.length} revenda(s) cadastrada(s)</p>
        <Button onClick={openNovo} data-testid="revenda-novo-lanc-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Lancamento
        </Button>
      </div>

      {revendas.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Nenhuma revenda cadastrada pelo administrador.</CardContent></Card>
      ) : (
        <Tabs defaultValue={revendas[0]?.id} className="w-full">
          <TabsList className="w-full flex-wrap h-auto">
            {revendas.map(r => (
              <TabsTrigger key={r.id} value={r.id} className="gap-1.5">
                <Store className="w-3.5 h-3.5" /> {r.nome}
              </TabsTrigger>
            ))}
          </TabsList>
          {revendas.map(r => (
            <TabsContent key={r.id} value={r.id} className="mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{r.nome}</CardTitle>
                    <Badge variant="outline">{r.lancamentos?.length || 0} lancamentos</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(!r.lancamentos || r.lancamentos.length === 0) ? (
                    <p className="text-sm text-muted-foreground text-center py-4">Sem lancamentos</p>
                  ) : (
                    r.lancamentos.map(l => (
                      <div key={l.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border/50">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium truncate">{l.servico || "—"}</p>
                            {l.tipo_credito && <Badge variant="secondary" className="text-[10px]">{l.tipo_credito}</Badge>}
                            {l.confirmado && <Badge className="text-[10px] bg-emerald-500/15 text-emerald-500 border-emerald-500/30">Confirmado</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground">{l.data} - {l.moeda || "R$"} {(l.valor || 0).toFixed(2)} {l.funcionario && `· por ${l.funcionario}`}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(l, r.id)} data-testid={`edit-revenda-lanc-${l.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(l)} data-testid={`delete-revenda-lanc-${l.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLanc ? "Editar Lancamento" : "Novo Lancamento de Revenda"}</DialogTitle>
            <DialogDescription>{editingLanc ? "Atualize os dados do lancamento." : "Adicione um lancamento a uma revenda ja cadastrada."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Revenda *</Label>
              <Select value={selectedRevendaId} onValueChange={setSelectedRevendaId} disabled={!!editingLanc}>
                <SelectTrigger data-testid="revenda-select"><SelectValue placeholder="Selecione a revenda" /></SelectTrigger>
                <SelectContent>
                  {revendas.map(r => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Tipo</Label>
                <Select value={form.tipo_credito} onValueChange={(v) => setForm({...form, tipo_credito: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MEC">MEC</SelectItem>
                    <SelectItem value="Creditos">Creditos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>MEC/Creditos</Label><Input value={form.mec_creditos} onChange={e => setForm({...form, mec_creditos: e.target.value})} /></div>
            </div>
            <div className="space-y-1"><Label>Servico *</Label><Input value={form.servico} onChange={e => setForm({...form, servico: e.target.value})} placeholder="Ex: Renovacao" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Valor *</Label><Input type="number" step="0.01" value={form.valor} onChange={e => setForm({...form, valor: e.target.value})} /></div>
              <div className="space-y-1">
                <Label>Moeda</Label>
                <Select value={form.moeda} onValueChange={(v) => setForm({...form, moeda: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="R$">R$</SelectItem>
                    <SelectItem value="€">€</SelectItem>
                    <SelectItem value="$">$</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1"><Label>Data</Label><Input type="date" value={form.data} onChange={e => setForm({...form, data: e.target.value})} /></div>
            <div className="space-y-1"><Label>Descricao</Label><Textarea value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="revenda-save-lanc-btn">{saving ? "Salvando..." : (editingLanc ? "Salvar" : "Adicionar")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ================== ACOMODADO SECTION ==================
function AcomodadoSection({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nome: "", endereco: "", cpf: "", telefone: "", quantidade_dispositivos: 1, codigo_launcher: "", observacao: "" });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/hub-empresa/funcionarios/publico/${token}/acomodado/list`);
      setItems(res.data);
    } catch { toast.error("Erro ao carregar"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm({ nome: "", endereco: "", cpf: "", telefone: "", quantidade_dispositivos: 1, codigo_launcher: "", observacao: "" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      nome: item.nome || "",
      endereco: item.endereco || "",
      cpf: item.cpf || "",
      telefone: item.telefone || "",
      quantidade_dispositivos: item.quantidade_dispositivos || 1,
      codigo_launcher: item.codigo_launcher || "",
      observacao: item.observacao || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.nome.trim()) return toast.error("Informe o nome");
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/api/hub-empresa/funcionarios/publico/${token}/acomodado/${editing.id}`, form);
        toast.success("Acomodado atualizado!");
      } else {
        await axios.post(`${API}/api/hub-empresa/funcionarios/publico/${token}/acomodado`, form);
        toast.success("Acomodado cadastrado!");
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Excluir "${item.nome}"?`)) return;
    try {
      await axios.delete(`${API}/api/hub-empresa/funcionarios/publico/${token}/acomodado/${item.id}`);
      toast.success("Excluido!");
      fetchData();
    } catch { toast.error("Erro ao excluir"); }
  };

  if (loading) return <p className="text-center py-8 text-muted-foreground">Carregando...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{items.length} acomodado(s)</p>
        <Button onClick={openCreate} data-testid="acomodado-novo-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Acomodado
        </Button>
      </div>

      {items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Nenhum acomodado cadastrado.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <Card key={item.id}>
              <CardContent className="p-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium truncate">{item.nome}</p>
                    <Badge variant="outline" className="text-[10px]">{item.quantidade_dispositivos || 1} disp.</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {item.telefone} {item.endereco && `· ${item.endereco}`}
                  </p>
                  {item.funcionario && <p className="text-[10px] text-muted-foreground">Por: {item.funcionario}</p>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(item)}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(item)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Acomodado" : "Novo Acomodado"}</DialogTitle>
            <DialogDescription>Cadastro de pessoa acomodada.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1"><Label>Nome *</Label><Input value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} /></div>
            <div className="space-y-1"><Label>Endereco</Label><Input value={form.endereco} onChange={e => setForm({...form, endereco: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>CPF</Label><Input value={form.cpf} onChange={e => setForm({...form, cpf: e.target.value})} /></div>
              <div className="space-y-1"><Label>Telefone</Label><Input value={form.telefone} onChange={e => setForm({...form, telefone: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Dispositivos</Label><Input type="number" min="1" value={form.quantidade_dispositivos} onChange={e => setForm({...form, quantidade_dispositivos: parseInt(e.target.value) || 1})} /></div>
              <div className="space-y-1"><Label>Codigo Launcher</Label><Input value={form.codigo_launcher} onChange={e => setForm({...form, codigo_launcher: e.target.value})} /></div>
            </div>
            <div className="space-y-1"><Label>Observacao</Label><Textarea value={form.observacao} onChange={e => setForm({...form, observacao: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="acomodado-save-btn">{saving ? "Salvando..." : (editing ? "Salvar" : "Adicionar")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ================== REV ACOMODADO SECTION ==================
function RevAcomodadoSection({ token }) {
  const [revendas, setRevendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRevendaId, setSelectedRevendaId] = useState("");
  const [editingCliente, setEditingCliente] = useState(null);
  const [form, setForm] = useState({ nome: "", endereco: "", telefone: "", quantidade_dispositivos: 1, valor: "", codigo_launcher: "", observacao: "" });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/hub-empresa/funcionarios/publico/${token}/rev-acomodado/list`);
      setRevendas(res.data);
    } catch { toast.error("Erro ao carregar"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setEditingCliente(null);
    setSelectedRevendaId("");
    setForm({ nome: "", endereco: "", telefone: "", quantidade_dispositivos: 1, valor: "", codigo_launcher: "", observacao: "" });
  };

  const openNovo = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (cliente, revendaId) => {
    setEditingCliente(cliente);
    setSelectedRevendaId(revendaId);
    setForm({
      nome: cliente.nome || "",
      endereco: cliente.endereco || "",
      telefone: cliente.telefone || "",
      quantidade_dispositivos: cliente.quantidade_dispositivos || 1,
      valor: String(cliente.valor || ""),
      codigo_launcher: cliente.codigo_launcher || "",
      observacao: cliente.observacao || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingCliente && !selectedRevendaId) return toast.error("Selecione a revenda");
    if (!form.nome.trim()) return toast.error("Informe o nome do cliente");
    setSaving(true);
    try {
      if (editingCliente) {
        await axios.put(`${API}/api/hub-empresa/funcionarios/publico/${token}/rev-acomodado/lancamento/${editingCliente.id}`, {
          ...form,
          valor: parseFloat(form.valor || 0),
        });
        toast.success("Cliente atualizado!");
      } else {
        await axios.post(`${API}/api/hub-empresa/funcionarios/publico/${token}/rev-acomodado/lancamento`, {
          ...form,
          revenda_id: selectedRevendaId,
          valor: parseFloat(form.valor || 0),
        });
        toast.success("Cliente adicionado!");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (cliente) => {
    if (!window.confirm(`Excluir cliente "${cliente.nome}"?`)) return;
    try {
      await axios.delete(`${API}/api/hub-empresa/funcionarios/publico/${token}/rev-acomodado/lancamento/${cliente.id}`);
      toast.success("Excluido!");
      fetchData();
    } catch { toast.error("Erro ao excluir"); }
  };

  if (loading) return <p className="text-center py-8 text-muted-foreground">Carregando...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{revendas.length} revenda(s) acomodado</p>
        <Button onClick={openNovo} data-testid="rev-aco-novo-cliente-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Cliente
        </Button>
      </div>

      {revendas.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Nenhuma revenda acomodado cadastrada.</CardContent></Card>
      ) : (
        <Tabs defaultValue={revendas[0]?.id} className="w-full">
          <TabsList className="w-full flex-wrap h-auto">
            {revendas.map(r => (
              <TabsTrigger key={r.id} value={r.id} className="gap-1.5">
                <Store className="w-3.5 h-3.5" /> {r.nome}
              </TabsTrigger>
            ))}
          </TabsList>
          {revendas.map(r => (
            <TabsContent key={r.id} value={r.id} className="mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base">{r.nome}</CardTitle>
                    <div className="flex gap-2">
                      <Badge variant="outline">{r.total_clientes} clientes</Badge>
                      <Badge variant="outline">{r.total_dispositivos} disp.</Badge>
                      <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30">{fmt(r.soma_valor)}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(!r.clientes || r.clientes.length === 0) ? (
                    <p className="text-sm text-muted-foreground text-center py-4">Sem clientes</p>
                  ) : (
                    r.clientes.map(c => (
                      <div key={c.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border/50">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium truncate">{c.nome}</p>
                            <Badge variant="secondary" className="text-[10px]">{c.quantidade_dispositivos} disp.</Badge>
                            <Badge className="text-[10px] bg-emerald-500/15 text-emerald-500 border-emerald-500/30">{fmt(c.valor)}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {c.telefone} {c.funcionario && `· por ${c.funcionario}`}
                          </p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(c, r.id)} data-testid={`edit-rev-aco-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(c)} data-testid={`delete-rev-aco-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCliente ? "Editar Cliente Acomodado" : "Novo Cliente Acomodado"}</DialogTitle>
            <DialogDescription>{editingCliente ? "Atualize os dados do cliente." : "Adicione um cliente a uma revenda acomodado ja cadastrada."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Revenda *</Label>
              <Select value={selectedRevendaId} onValueChange={setSelectedRevendaId} disabled={!!editingCliente}>
                <SelectTrigger data-testid="rev-aco-select"><SelectValue placeholder="Selecione a revenda" /></SelectTrigger>
                <SelectContent>
                  {revendas.map(r => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Nome *</Label><Input value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} /></div>
            <div className="space-y-1"><Label>Endereco</Label><Input value={form.endereco} onChange={e => setForm({...form, endereco: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Telefone</Label><Input value={form.telefone} onChange={e => setForm({...form, telefone: e.target.value})} /></div>
              <div className="space-y-1"><Label>Dispositivos</Label><Input type="number" min="1" value={form.quantidade_dispositivos} onChange={e => setForm({...form, quantidade_dispositivos: parseInt(e.target.value) || 1})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Valor</Label><Input type="number" step="0.01" value={form.valor} onChange={e => setForm({...form, valor: e.target.value})} /></div>
              <div className="space-y-1"><Label>Codigo Launcher</Label><Input value={form.codigo_launcher} onChange={e => setForm({...form, codigo_launcher: e.target.value})} /></div>
            </div>
            <div className="space-y-1"><Label>Observacao</Label><Textarea value={form.observacao} onChange={e => setForm({...form, observacao: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="rev-aco-save-cliente-btn">{saving ? "Salvando..." : (editingCliente ? "Salvar" : "Adicionar")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
